import classNames from 'classnames'
import { motion } from 'framer-motion'

import {
  buildAlarmControlIcon,
  buildGenericControlIcon,
  buildStateControlIcon
} from '@apps-common/apps/portal/pages/units/pages/manage/components/controls/control-buttons'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { VertSpacerMd } from '@apps-common/components/layout'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import {
  CONTROL_STATE_OFF,
  CONTROL_STATE_ON,
  CONTROL_TRIGGER_TYPE_LATCH,
  CONTROL_TYPE_ACCESS_CONTROL,
  CONTROL_TYPE_ALARM,
  isControlOff,
  isControlOn
} from '@apps-common/config/controls'
import { commonIcons } from '@apps-common/config/icons'
import { UNIT_STATUS_CANCELLED } from '@apps-common/config/unit-filter'
import { useControlsContext } from '@apps-common/contexts/ControlContext'
import { useControlTheme } from '@apps-common/hooks/useControlTheme'
import { IUnitControlWithStateModel } from '@apps-common/types/portal/unit-control'

import { useMyMonitoringUpdateDefaultControl } from '@apps-common/apis/my-monitoring/control'
import { OptionsModalContainer, OptionsModalHeader } from '@apps-common/components/modals/options-modals'
import { ModalDismiss } from '@apps-common/config/modal'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { ENV_APP_PROJECT } from '@apps-common/lib/environment'
import { invalidateControlListQueries } from '@apps-common/lib/invalidateControlQueries'
import { useMyMonitoringSiteContextStore } from '@apps-common/stores'
import { useMemo } from 'react'
import { getControlType } from './control-type'

const project = ENV_APP_PROJECT

export interface IControlOptionsModal {
  unitStatus: string
  isActivated: boolean
  control: IUnitControlWithStateModel
  showDefaultControlOptions?: boolean
}

export const ControlOptionsModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IControlOptionsModal
}) => {
  const { isAdmin: isMyMonitoringAdmin } = useMyMonitoringSiteContextStore()
  const { unitStatus, isActivated, control, showDefaultControlOptions = false } = componentProps
  const { controlTypeId, triggerType, requiresControlState, currentState, controlName } = control || {}
  const { awaiting, getAwaiting, subscribe, unsubscribe } = useControlsContext()
  const { resolve } = useControlTheme()

  const isAdminOrPortalUser = useMemo(() => {
    if (project === 'my-monitoring') {
      return isMyMonitoringAdmin
    }

    return true
  }, [project, isMyMonitoringAdmin])

  const defaultControlHook = useProjectHook(useMyMonitoringUpdateDefaultControl, useMyMonitoringUpdateDefaultControl)

  const { mutate: updateDefaultControl, isPending: updateDefaultControlIsPending } = defaultControlHook(true, () => {
    invalidateControlListQueries(control?.unitId)
    onDismiss(null, ModalDismiss.Cancel)
  })

  const controlType = getControlType(controlTypeId, requiresControlState)
  const colors = resolve(controlType, currentState.toLowerCase())

  console.log('colors', colors)

  const isAwaiting = getAwaiting(control.unitControlId) !== undefined

  const loading = isAwaiting || !isActivated || updateDefaultControlIsPending

  const buildAlarmControlOptions = () => {
    const state = currentState?.toLowerCase()
    const options = []

    const SendArmButton = (
      <ButtonRoundedSm
        type='button'
        loading={loading}
        icon={<commonIcons.armed />}
        className={classNames('w-full', colors.palette?.onBgColor, colors.palette?.onTextColor)}
        callback={() => onDismiss(JSON.stringify({ action: 'activate', command: CONTROL_STATE_ON }), 'confirm')}
        text='Arm'
      />
    )

    const SendDisarmButton = (
      <ButtonRoundedSm
        type='button'
        loading={loading}
        icon={<commonIcons.disarmed />}
        className={classNames('w-full', colors.palette?.offBgColor, colors.palette?.offTextColor)}
        callback={() => onDismiss(JSON.stringify({ action: 'activate', command: CONTROL_STATE_OFF }), 'confirm')}
        text='Disarm'
      />
    )

    if (state === CONTROL_STATE_ON) {
      options.push(SendDisarmButton)
    } else if (state === CONTROL_STATE_OFF) {
      options.push(SendArmButton)
    } else {
      options.push(SendArmButton)
      options.push(SendDisarmButton)
    }

    return options
  }

  const buildAccessControlOptions = () => {
    const state = currentState?.toLowerCase()
    const options = []

    const SendArmButton = (
      <ButtonRoundedSm
        type='button'
        loading={loading}
        icon={<commonIcons.armed />}
        className={classNames('w-full', colors.palette?.onBgColor, colors.palette?.onTextColor)}
        callback={() => onDismiss(JSON.stringify({ action: 'activate', command: CONTROL_STATE_ON }), 'confirm')}
        text='Lock'
      />
    )

    const SendDisarmButton = (
      <ButtonRoundedSm
        type='button'
        loading={loading}
        icon={<commonIcons.disarmed />}
        className={classNames('w-full', colors.palette?.offBgColor, colors.palette?.offTextColor)}
        callback={() => onDismiss(JSON.stringify({ action: 'activate', command: CONTROL_STATE_OFF }), 'confirm')}
        text='Unlock'
      />
    )

    if (state === CONTROL_STATE_ON) {
      options.push(SendDisarmButton)
    } else if (state === CONTROL_STATE_OFF) {
      options.push(SendArmButton)
    } else {
      options.push(SendArmButton)
      options.push(SendDisarmButton)
    }

    return options
  }

  const buildCustomControlWithStateOptions = () => {
    const state = currentState?.toLowerCase()
    const options = []

    const SendOnButton = (
      <ButtonRoundedSm
        type='button'
        loading={loading}
        icon={<commonIcons.powerAlt />}
        className={classNames('w-full', colors.palette?.onBgColor, colors.palette?.onTextBgColor)}
        callback={() => onDismiss(JSON.stringify({ action: 'activate', command: CONTROL_STATE_ON }), 'confirm')}
        text='Turn On'
      />
    )

    const SendOffButton = (
      <ButtonRoundedSm
        type='button'
        loading={loading}
        icon={<commonIcons.powerOffAlt />}
        className={classNames('w-full', colors.palette?.offBgColor, colors.palette?.offTextColor)}
        callback={() => onDismiss(JSON.stringify({ action: 'activate', command: CONTROL_STATE_OFF }), 'confirm')}
        text='Turn Off'
      />
    )

    if (state === CONTROL_STATE_ON) {
      options.push(SendOffButton)
    } else if (state === CONTROL_STATE_OFF) {
      options.push(SendOnButton)
    } else {
      options.push(SendOnButton)
      options.push(SendOffButton)
    }

    return options
  }

  console.log(colors)

  const buildCustomControlWithoutStateOptions = () => {
    const options = []

    if (triggerType === CONTROL_TRIGGER_TYPE_LATCH.value) {
      options.push(
        <ButtonRoundedSm
          type='button'
          loading={loading}
          icon={<commonIcons.powerAlt />}
          className={classNames('w-full', colors.palette?.onBgColor, colors.palette?.onTextColor)}
          callback={() => onDismiss(JSON.stringify({ action: 'activate', command: CONTROL_STATE_ON }), 'confirm')}
          text='Turn On'
        />
      )
      options.push(
        <ButtonRoundedSm
          type='button'
          loading={loading}
          icon={<commonIcons.powerOffAlt />}
          className={classNames('w-full', colors.palette?.offBgColor, colors.palette?.offTextColor)}
          callback={() => onDismiss(JSON.stringify({ action: 'activate', command: CONTROL_STATE_OFF }), 'confirm')}
          text='Turn Off'
        />
      )
    } else {
      options.push(
        <ButtonRoundedSm
          type='button'
          loading={loading}
          icon={<commonIcons.powerAlt />}
          className={classNames('w-full', colors.palette?.onBgColor, colors.palette?.onTextColor)}
          callback={() => onDismiss(JSON.stringify({ action: 'activate', command: CONTROL_STATE_ON }), 'confirm')}
          text='Turn On'
        />
      )
    }

    return options
  }

  const buildOptions = () => {
    let options = []

    if (controlTypeId === CONTROL_TYPE_ALARM) {
      options = buildAlarmControlOptions()
    } else if (controlTypeId === CONTROL_TYPE_ACCESS_CONTROL) {
      options = buildAccessControlOptions()
    } else if (requiresControlState) {
      options = buildCustomControlWithStateOptions()
    } else {
      options = buildCustomControlWithoutStateOptions()
    }

    return (
      <>
        <div className='flex flex-col gap-[10px]'>{options}</div>
        <div className='border-t border-border w-full mx-auto my-[10px]' />

        <ButtonRoundedSm
          type='button'
          loading={false}
          icon={<commonIcons.history className='text-[1rem]' />}
          className='w-full action-info'
          callback={() => onDismiss(JSON.stringify({ action: 'history' }), 'confirm')}
          text='History'
        />
        <div className='border-t border-border w-full mx-auto my-[10px]' />
        {isAdminOrPortalUser && (
          <>
            <ButtonRoundedSm
              type='button'
              loading={unitStatus === UNIT_STATUS_CANCELLED.value || isAwaiting || updateDefaultControlIsPending}
              icon={<commonIcons.edit className='text-[1rem]' />}
              className='w-full action-warning'
              callback={() => onDismiss(JSON.stringify({ action: 'edit' }), 'confirm')}
              text='Edit'
            />
            <VertSpacerMd />
            <ModalButton
              component={ConfirmModal}
              disabled={false}
              componentProps={{
                text: 'Delete',
                description: 'Confirm you would like to delete this control. This cannot be undone.',
                action: ConfirmModalAction.Error
              }}
              classes='w-full'
              confirmCallback={() => {
                onDismiss(JSON.stringify({ action: 'delete' }), 'confirm')
              }}
            >
              <ButtonRoundedSm
                type='div'
                loading={isAwaiting || updateDefaultControlIsPending}
                icon={<commonIcons.delete className='text-[0.9rem]' />}
                className='w-full action-error'
                text='Delete'
              />
            </ModalButton>
            <VertSpacerMd />
            {showDefaultControlOptions && (
              <>
                <ButtonRoundedSm
                  type='button'
                  loading={updateDefaultControlIsPending}
                  icon={<commonIcons.pinUnit className='text-[1rem]' />}
                  className='w-full action-neutral'
                  callback={() => updateDefaultControl({ model: control })}
                  text='Make default'
                />
              </>
            )}
          </>
        )}
        <div className='border-t border-border w-full mx-auto my-[10px]' />
        <ButtonRoundedSm
          type='button'
          loading={false}
          icon={<commonIcons.close className='text-[1rem]' />}
          className='w-full action-neutral'
          callback={() => onDismiss(null, 'cancel')}
          text='Cancel'
        />
      </>
    )
  }

  const buildIcon = () => {
    if (controlTypeId === CONTROL_TYPE_ALARM || controlTypeId === CONTROL_TYPE_ACCESS_CONTROL) {
      return buildAlarmControlIcon(control, colors)
    }
    if (requiresControlState) {
      return buildStateControlIcon(control, colors)
    }

    return buildGenericControlIcon(colors)
  }

  const isOn = isControlOn(currentState)
  const isOff = isControlOff(currentState)

  return (
    <OptionsModalContainer onDismiss={onDismiss}>
      <motion.div layout className='bg-surface-2 px-[2rem] py-[1.25rem] rounded-3xl w-full'>
        <div className='max-w-[220px] sm:w-[180px] flex flex-col mx-auto'>
          <OptionsModalHeader
            IconComponent={
              <>
                <div className='absolute mx-auto'>
                  <div className={classNames(isAwaiting && 'control-spinner', isOn && `control-spinner-reverse`, colors.border)} />
                </div>
                <div className={classNames('w-[50px] h-[50px] rounded-[25px] flex items-center justify-center', colors.bg)}>
                  {buildIcon()}
                </div>
              </>
            }
            subtitle='Control'
            title={controlName}
            someOptionsDisabled={isAwaiting}
          />
          {buildOptions()}
        </div>
      </motion.div>
    </OptionsModalContainer>
  )
}
